/** @jsxImportSource @emotion/react */

import React, { useState, useEffect, useRef } from 'react';
import Stomp from 'stompjs';

import { useNavigate, Navigate, Outlet } from 'react-router-dom';

import userStore from '@stores/userStore';
import chatStore from '@stores/chatStore';
import ChattingRoom from '../chat/ChattingRoom';
import { filterFloatingChatRooms } from '../chat/inquiryChatDockPolicy';
import deviceStore from '@app/stores/deviceStore';
import notificationStore from '@app/stores/notificationStore';
import { isLooseEqual } from '@app/utils/equality';
import { getStompCredentials } from '@app/utils/stompCredentials';
import {
  applyStompHeartbeat,
  createStompReconnector,
} from '@app/utils/stompReconnect';
import usePushHotelSwitch from '@app/hooks/usePushHotelSwitch';

/** 직원 가입 신청 알림 종류. 서버는 JOIN_REQ로 보내며, USER_JOIN은 기존 호환을 위해 함께 받는다. */
const JOIN_REQUEST_SUB_TYPES = ['JOIN_REQ', 'USER_JOIN'];



const debug = false;
const wsConfig = {
  wsurl: process.env.REACT_APP_WS_URL,
  wssurl: process.env.REACT_APP_WSS_URL,
  // subs: [process.env.REACT_APP_WS_SUB, process.env.REACT_APP_WS_SUB_COMMON],
  subs: [process.env.REACT_APP_WS_SUB],
};

export default function AuthLayout() {
  const navigate = useNavigate();
  const { accessToken, myInfo } = userStore();
  const {
    openChattingRoom,
    chattingRooms,
    setIncomeChat,
    readMessage,
    resetRooms,
    resetStore,
    dockedRoomId,
  } = chatStore();
  const { incomeNoti } = notificationStore();
  // CSV-02: 다른 접근 호텔의 푸시 알림이면 전환을 확인한 뒤 엽니다.
  const { interceptPush, confirmElement } = usePushHotelSwitch(webOnMessage);
  const { uuid } = deviceStore();
  const [mainStompClient, setMainStompClient] = useState(null);
  const [mainSubscribions, setMainSubscribions] = useState([]);
  const [connectCnt, setConnectCnt] = useState(0);
  const floatingChattingRooms = filterFloatingChatRooms(
    chattingRooms,
    dockedRoomId,
  );
  const [notice] = useState({
  startDate: new Date('2025-4-24'),
  endDate: new Date('2025-5-31'),
  contentsHotels: '홈즈스테이 지밸리 가산 / 베르누이호텔\n/ 88호텔 / 더 리센츠호텔동대문 / 골든씨티호텔\n/ 노블레스관광호텔 / 파크마린호텔 / MD호텔 독산'
});
  // 마운트 1회 등록·해제 계약과 accessToken 변경 시점은 유지하고 당시 필요한 참조만 보관합니다.
  const mountLifecycleRef = useRef(null);
  const connectionRef = useRef(null);
  const reconnectorRef = useRef(null);
  const unmountedRef = useRef(false);
  if (reconnectorRef.current === null) {
    // 실패할수록 길게 기다리고 가려진 탭은 다시 보일 때 접속합니다(브로커 접속 폭주 완화).
    reconnectorRef.current = createStompReconnector({
      connect: () => {
        debug &&
          console.log(`🚥 🚥 🚥 [MAIN]  Will reconnect 🚥 🚥 🚥`);
        connectionRef.current.connectMainStomp();
      },
      canReconnect: () =>
        !unmountedRef.current && Boolean(connectionRef.current?.accessToken),
    });
  }
  if (mountLifecycleRef.current === null) {
    mountLifecycleRef.current = {
      connectMainStomp,
      mainStompClient,
      resetStore,
      webOnMessage,
    };
  }
  connectionRef.current = {
    accessToken,
    connectMainStomp,
    disconnect,
    mainStompClient,
  };
  useEffect(() => {
    unmountedRef.current = false;
    reconnectorRef.current.start();
    const lifecycle = mountLifecycleRef.current;
    // HTTP 비보안 환경에는 서비스 워커가 없을 수 있으므로, 사용 가능한 경우에만 등록한다.
    const serviceWorker = 'serviceWorker' in navigator && navigator.serviceWorker;
    const handleServiceWorkerMessage = (event) => {
      const data = event.data; // 수신된 데이터
      if (!data.isFirebaseMessaging) {
        // 수신된 데이터를 처리
        console.log('[AuthLayout.js] 서비스 워커로부터 수신한 데이터 : ', data);
        // alert('서비스 워커로부터 수신');
        lifecycle.webOnMessage(data);
      }
    };
    if (serviceWorker) {
      serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    if (lifecycle.mainStompClient == null) {
      window.connectCount = 0;
      lifecycle.connectMainStomp();
    }

    getPushPayload();

    return () => {
      unmountedRef.current = true;
      reconnectorRef.current.stop();
      // 다시 마운트될 때 동일 메시지를 중복 처리하지 않도록 등록한 리스너를 해제한다.
      if (serviceWorker) {
        serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
      const connection = connectionRef.current;
      if (connection.mainStompClient) {
        connection.disconnect(connection.mainStompClient, '[MAIN]');
      }
      lifecycle.resetStore();
    };
  }, []);

  useEffect(() => {
    debug && console.log('[MAIN] 📖 subscribions : ', mainSubscribions);
  }, [mainSubscribions]);

  useEffect(() => {
    false &&
      console.log(
        'chattingRooms Focus : ',
        JSON.stringify(chattingRooms.map((test) => test.focus)),
      );
  }, [chattingRooms]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!accessToken) {
      reconnectorRef.current.cancel();
      if (connection.mainStompClient) {
        connection.disconnect(connection.mainStompClient, '[MAIN]');
      }
      // if (subStompClient) disconnect(subStompClient, '[SUB]');
    }
  }, [accessToken]);

  useEffect(() => {
    false && console.log('connectCnt : ', connectCnt);
    if (connectCnt > 1) {
      resetRooms();
    }
  }, [connectCnt, resetRooms]);

  // 웹에서 수신
  function webOnMessage(message) {
    if (interceptPush(message)) return;
    if (isLooseEqual(message?.subType, 'CHAT')) {
      openChattingRoom(message.contentsId);
    } else if (
      message?.subType === 'PARTNERS_REQ' &&
      isLooseEqual(myInfo?.authority, 'ROLE_HOTEL_MANAGER')
    ) {
      navigate('/setting/travel-group');
    } else if (
      JOIN_REQUEST_SUB_TYPES.includes(message?.subType) &&
      isLooseEqual(myInfo?.authority, 'ROLE_HOTEL_MANAGER')
    ) {
      navigate('/setting/employee');
    } else if (isLooseEqual(message?.subType, 'RESERVATION')) {
      navigate(`/inquiry?id=${message.contentsId}`);
    }
  }

  function getPushPayload() {
    const serviceWorkerScope = '/firebase-cloud-messaging-push-scope/';
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistration(serviceWorkerScope)
        .then((registration) => {
          if (registration) {
            console.log(
              '[AuthLayout.js] Service Worker registration found:',
              registration,
            );

            // 서비스 워커가 활성 상태인지 확인
            const serviceWorker =
              registration.active ||
              registration.waiting ||
              registration.installing;

            if (serviceWorker) {
              // 서비스 워커가 활성 상태라면 메시지 보내기
              console.log(
                '[AuthLayout.js] Service Worker is active:',
                serviceWorker,
              );
              serviceWorker.postMessage({
                type: 'GET_PUSH_PAYLOAD',
              });
            } else {
              // 서비스 워커가 활성 상태가 아니라면 활성 상태가 될 때까지 기다림
              console.log('[AuthLayout.js] Service Worker is not active yet.');
              registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;

                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'activated') {
                    console.log(
                      '[AuthLayout.js] Service Worker is now active:',
                      newWorker,
                    );
                    newWorker.postMessage({
                      type: 'GET_PUSH_PAYLOAD',
                    });
                  }
                });
              });
            }
          } else {
            console.error(
              '[AuthLayout.js] No Service Worker registration found for scope:',
              serviceWorkerScope,
            );
          }
        })
        .catch((error) => {
          console.error(
            '[AuthLayout.js] Error fetching Service Worker registration:',
            error,
          );
        });
    }
  }

  function connectMainStomp() {
    console.log('[MAIN] will connect Stomp');
    if (!myInfo) {
      debug && console.warn('pleas login');
      return;
    }

    // Create connection and subscription
    if (!window.WebSocket) return;

    debug && console.log('wsConfig : ', wsConfig);
    const mainClient = Stomp.over(new WebSocket(wsConfig.wssurl));
    mainClient.debug = null; // 디버깅 필요 할 때 주석처리 하면 연결상태, 데이터 보임
    applyStompHeartbeat(mainClient);
    // SEC-MQ: 토큰 모드이면 공용 계정 대신 로그인 사용자의 JWT로 접속한다.
    const credentials = getStompCredentials({
      userNo: myInfo.userNo,
      accessToken: connectionRef.current.accessToken,
      url: wsConfig.wssurl,
    });
    mainClient.connect(
      credentials.login,
      credentials.passcode,
      () => {
        if (unmountedRef.current || !connectionRef.current.accessToken) {
          mainClient.disconnect();
          return;
        }
        debug && console.log('[MAIN] stomp connect success');

        setConnectCnt(window.connectCount + 1);
        window.connectCount = window.connectCount + 1;
        reconnectorRef.current.connected();

        let newSubscribes = [];
        wsConfig.subs.forEach((sub, i) => {
          const subscribsion = sub.replace('*hotelId', myInfo.hotel.hotelId);

          console.log('[MAIN] QUEUE ID : ', `${uuid}.${myInfo.userNo}.${i}`);

          const newSubscribe = mainClient.subscribe(
            subscribsion,
            (msg) => {
              debug && console.log('[MAIN] msg : ', msg);
              const message = JSON.parse(msg.body);
              debug && console.log('[MAIN] msg.body : ', message);

              if (isLooseEqual(message.messageType, 'CHAT_REZ')) {
                const chatRezMessage = JSON.parse(msg.body).chatRezMessage;
                debug &&
                  console.log('[MAIN] Income Message : ', chatRezMessage);
                if (isLooseEqual(chatRezMessage.messageType, 'MESSAGE')) {
                  setIncomeChat(chatRezMessage);
                } else if (isLooseEqual(chatRezMessage.messageType, 'READ')) {
                  readMessage(chatRezMessage.reservationId, chatRezMessage.seq);
                }
              } else if (isLooseEqual(message.messageType, 'ALARM')) {
                const pushNoti = message.pushNoti;
                debug && console.log('[MAIN] Income PushNoti : ', pushNoti);
                incomeNoti(pushNoti);
              }
            },
            {
              id: `${uuid}.${myInfo.userNo}.${i}`,
              'auto-delete': true,
              durable: false,
              exclusive: false,
            },
          );
          newSubscribes = [...newSubscribes, newSubscribe];
        });
        setMainSubscribions(newSubscribes);
        setMainStompClient(mainClient);
      },
      (e) => {
        debug && console.error('[MAIN] Stomp error!!', e);
        connectionRef.current.disconnect(mainClient, '[MAIN]');
        scheduleReconnect();
        setMainStompClient(null);
      },
    );
  }

  function scheduleReconnect() {
    reconnectorRef.current.schedule();
  }

  function disconnect(stomp, str) {
    // 재접속 예약은 여기서 지우지 않습니다. 오류 직후 연속 호출에도 예약이 하나로 유지되고,
    // 화면 종료·로그아웃은 각 경로에서 reconnectorRef로 직접 취소합니다.
    console.log(str + ' will disconnect..');
    mainSubscribions.forEach((subscribion) => {
      subscribion.unsubscribe();
    });

    if (stomp) {
      stomp.disconnect(() => {
        if (!unmountedRef.current) {
          setMainStompClient(null);
          setMainSubscribions([]);
        }
        debug && console.log('disconnect stomp');
      });
    }
  }

  const [viewNotice, setViewNotice] = useState(false);

  useEffect(() => {
    if (notice) {
      const today = new Date(); // 오늘 날짜
      // today.setDate(today.getDate() + 1); // 현재 날짜 + 1일
      const targetStartDate = notice.startDate; // 비교할 날짜
      const targetEndDate = notice.endDate; // 비교할 날짜
      today.setHours(0, 0, 0, 0);
      targetStartDate.setHours(0, 0, 0, 0);
      targetEndDate.setHours(0, 0, 0, 0);

      // 로컬스토리지에서 "hideNoticeUntil" 키로 저장된 날짜 확인
      const hiddenUntil = localStorage.getItem('hideNoticeUntil');
      if (hiddenUntil) {
        const hiddenDate = new Date(hiddenUntil);
        if (today <= hiddenDate) {
          setViewNotice(false);
          return;
        }
      }

      if (today >= targetStartDate && today <= targetEndDate) {
        setViewNotice(true);
      }
    }
  }, [notice]);

  // "오늘 하루 안보기" 버튼 클릭 핸들러
  const handleHideForToday = () => {
    const today = new Date();

    today.setHours(0, 0, 0, 0); // 00:00:00으로 설정
    localStorage.setItem('hideNoticeUntil', today.toISOString()); // 날짜를 ISO 문자열로 저장
    setViewNotice(false); // 즉시 숨기기
  };

  return (
    <>
      {viewNotice && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            zIndex: '9999',
            backgroundColor: '#FFF',
            border: '1px solid #767676',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div
            style={{
              maxWidth: '640px',
              padding: '30px',
              backgroundColor: '#C6F0FA',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                fontSize: '40px',
                lineHeight: '27px',
                marginLeft: '-2px',
                fontWeight: '700',
                color: '#FF946E',
              }}
            >
              NOTICE
            </span>
            <div
              style={{
                border: '5px solid #FF946E',
                padding: '20px 30px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <span
                style={{
                  fontSize: '30px',
                  lineHeight: '30px',
                  fontWeight: '700',
                  color: '#333',
                  textAlign: 'center',
                }}
              >
                문의관련안내
              </span>
              <span
                style={{
                  fontSize: '24px',
                  lineHeight: '30px',
                  fontWeight: '500',
                  color: '#000000',
                  marginTop: '40px',
                }}
              >
                O.C.C 견적 및 예약 문의 가능한 호텔 안내 드립니다.
              </span>
              <span
                style={{
                  fontSize: '24px',
                  lineHeight: '30px',
                  fontWeight: '500',
                  color: '#000000',
                  marginTop: '12px',
                }}
              >
                문의 가능 호텔
              </span>
              <span
                style={{
                  fontSize: '24px',
                  lineHeight: '30px',
                  fontWeight: '700',
                  marginTop: '22px',
                  color: '#FF946E',
                  whiteSpace: 'break-spaces',
                  letterSpacing: '0px',
                }}
              >
                {notice?.contentsHotels || ''}
              </span>
              <span
                style={{
                  fontSize: '24px',
                  lineHeight: '30px',
                  fontWeight: '500',
                  color: '#000000',
                  marginTop: '22px',
                }}
              >
                많은 문의 부탁드립니다.
              </span>
              <span
                style={{
                  fontSize: '24px',
                  lineHeight: '30px',
                  fontWeight: '600',
                  color: '#555',
                  marginTop: '12px',
                  letterSpacing: '0px',
                  wordBreak: 'keep-all',
                }}
              >
                * 해당 호텔외에 문의가 불가능 하오니 이용하실 때 참고 바랍니다.
              </span>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              borderTop: '1px solid #767676',
            }}
          >
            <div
              css={[styles.notiButtonWrap]}
              style={{
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '54px',
                cursor: 'pointer',
              }}
              onClick={handleHideForToday}
            >
              <span style={{ fontSize: '14px', color: '#777' }}>
                오늘 하루 안보기
              </span>
            </div>
            <div
              css={[styles.notiButtonWrap]}
              style={{
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '54px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setViewNotice(false);
              }}
            >
              <span style={{ fontSize: '14px', color: '#777' }}>닫기</span>
            </div>
          </div>
        </div>
      )}
      {accessToken && myInfo ? (
        <Outlet />
      ) : (
        <Navigate to="/login" replace="true" />
      )}
      {confirmElement}
      {floatingChattingRooms.length > 0 && (
        <div css={[styles.globalPopupList]}>
          {floatingChattingRooms.map((room, i) => {
            // 예약 ID를 key로 써서 앞 창을 닫아도 다른 창의 드래그 좌표가 재사용되지 않게 합니다.
            return <ChattingRoom key={room.id} chattingRoom={room} index={i} />;
          })}
        </div>
      )}
    </>
  );
}

const styles = {
  globalPopupList: {
    pointerEvents: 'none',
    overflowX: 'auto',
    maxWidth: '100%',
    height: '100%',
    boxSizing: 'border-box',
    position: 'fixed',
    right: '0',
    bottom: '40px',
    zIndex: '1000',
    display: 'flex',
    alignItems: 'end',
    gap: '10px',
    padding: '0 20px',
  },
  notiButtonWrap: {
    '&:hover': {
      '& span': {
        fontWeight: '700',
      },
    },
  },
};
